// File: src/utils/nominatim/nominatimClient.js
// Vue 2 / webpack friendly Nominatim client (no import.meta, no ?. operator).
// Defaults to brokered path: /svc/api/nominatim
//
// Env/runtime overrides:
//   VUE_APP_NOMINATIM_PATH or NOMINATIM_URL   (e.g. "https://svc.example.com/svc/api/nominatim")
//   window.__NOMINATIM_URL__                  (runtime override)

function getDefaultNomBase() {
    let url = ''
    try {
        if (typeof process !== 'undefined' && process.env) {
            url = process.env.VUE_APP_NOMINATIM_PATH || process.env.NOMINATIM_URL || ''
        }
        if (!url && typeof window !== 'undefined' && window.__NOMINATIM_URL__) {
            url = window.__NOMINATIM_URL__
        }
    } catch (e) { /* ignore */ }
    // DEFAULT: brokered path on this app (webpack devServer forwards /svc -> 127.0.0.1:3002)
    return url || '/svc/api/nominatim'
}

function trimTrailing(s) { return String(s || '').replace(/\/+$/, '') }
function trimLeading(s) { return String(s || '').replace(/^\/+/, '') }
function joinBase(base, pth) {
    const b = trimTrailing(base)
    const p = trimLeading(pth)
    return b ? `${b}/${p}` : `/${p}`
}

function toCountrycodes(cc) {
    if (!cc) return undefined
    if (Array.isArray(cc)) return cc.map(String).join(',').toLowerCase()
    return String(cc).toLowerCase()
}

function normLang(lang) {
    const s = String(lang || '').trim()
    return s || 'es' // safe fallback; your broker also supports per-request 'lang'
}

function buildUrl(base, path, params) {
    const url = joinBase(base, path)
    const qs = new URLSearchParams()
    for (const k in params) {
        const v = params[k]
        if (v === undefined || v === null || v === '') continue
        qs.set(k, String(v))
    }
    const q = qs.toString()
    return q ? `${url}?${q}` : url
}

function fetchJson(url, opts) {
    return fetch(url, Object.assign({
        method: 'GET',
        headers: { Accept: 'application/json' },
        cache: 'no-store',
        credentials: 'omit',
        redirect: 'manual',
    }, opts || {})).then(async res => {
        if (res.type === 'opaqueredirect' || (res.status >= 300 && res.status < 400)) {
            throw new Error('redirect')
        }
        if (!res.ok) {
            const msg = await res.text().catch(() => '')
            throw new Error(`Nominatim ${res.status}: ${msg}`.trim())
        }
        return res.json()
    })
}

/**
 * Free-text search (Nominatim /search)
 * @param {string} query - free text (address)
 * @param {{ lang?: string, countrycodes?: string|string[], addressdetails?: 0|1, limit?: number, viewbox?: string, bounded?: 0|1, baseUrl?: string, extra?: Object }} opts
 */
export function nomSearch(query, opts) {
    opts = opts || {}
    const base = opts.baseUrl || getDefaultNomBase()
    const params = Object.assign({
        format: 'jsonv2',
        q: String(query || ''),
        addressdetails: opts.addressdetails == null ? 1 : opts.addressdetails,
        countrycodes: toCountrycodes(opts.countrycodes),
        lang: normLang(opts.lang),
        limit: opts.limit || 10,
    }, opts.extra || {})

    // Optional viewport narrowing if provided
    if (opts.viewbox) params.viewbox = String(opts.viewbox)
    if (typeof opts.bounded !== 'undefined') params.bounded = opts.bounded ? 1 : 0

    const url = buildUrl(base, 'search', params)
    return fetchJson(url)
}

/**
 * Structured search (Nominatim /search?street=...&city=... etc.)
 * @param {{ house_number?:string, road?:string, city?:string, locality?:string, state?:string, postcode?:string, country?:string }} address
 * @param {{ lang?: string, countrycodes?: string|string[], addressdetails?: 0|1, limit?: number, baseUrl?: string, extra?: Object }} opts
 */
export function nomSearchStructured(address, opts) {
    address = address || {}
    opts = opts || {}
    const base = opts.baseUrl || getDefaultNomBase()

    const street =
        address.house_number && address.road ? `${address.house_number} ${address.road}` :
            address.road ? address.road : undefined

    const params = Object.assign({
        format: 'jsonv2',
        addressdetails: opts.addressdetails == null ? 1 : opts.addressdetails,
        countrycodes: toCountrycodes(opts.countrycodes),
        lang: normLang(opts.lang),
        street,
        city: address.city || address.locality,
        state: address.state,
        postalcode: address.postcode,
        country: address.country,
        limit: opts.limit || 10,
    }, opts.extra || {})

    const url = buildUrl(base, 'search', params)
    return fetchJson(url)
}

/**
 * Reverse geocode (Nominatim /reverse)
 * @param {{ lat:number, lon:number }} coords
 * @param {{ lang?: string, countrycodes?: string|string[], zoom?: number, addressdetails?: 0|1, baseUrl?: string, extra?: Object }} opts
 */
export function nomReverse(coords, opts) {
    opts = opts || {}
    const base = opts.baseUrl || getDefaultNomBase()

    const params = Object.assign({
        format: 'jsonv2',
        lat: coords && coords.lat != null ? coords.lat : (coords && coords.latitude),
        lon: coords && coords.lon != null ? coords.lon : (coords && coords.longitude),
        addressdetails: opts.addressdetails == null ? 1 : opts.addressdetails,
        countrycodes: toCountrycodes(opts.countrycodes),
        lang: normLang(opts.lang),
    }, opts.extra || {})

    if (opts.zoom != null) params.zoom = opts.zoom
    const url = buildUrl(base, 'reverse', params)
    return fetchJson(url)
}

export default { nomSearch, nomSearchStructured, nomReverse }
